import { action, observable, makeObservable } from 'mobx';
import * as Models from 'Models/Entities';
import { Model, IModelAttributes, entity, attribute } from 'Models/Model';
import { isNotNullOrUndefined } from '../../Util/TypeGuards';

export interface ICheckInStatusHistoryEntityAttributes extends IModelAttributes {
	checkInStatusId: string;
	userId: string;
	bookingId: string;
	stopId: string;
	checkInStatus: Models.CheckInStatusEntity | Models.ICheckInStatusEntityAttributes;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	booking: Models.BookingEntity | Models.IBookingEntityAttributes;
	stop: Models.StopEntity | Models.IStopEntityAttributes;
}

@entity('CheckInStatusHistoryEntity', 'Check-in Status History')
export default class CheckInStatusHistoryEntity extends Model implements ICheckInStatusHistoryEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute()
	public checkInStatusId: string;

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute()
	public bookingId: string;

	@observable
	@attribute()
	public stopId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public checkInStatus: Models.CheckInStatusEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public booking: Models.BookingEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public stop: Models.StopEntity;

	constructor(attributes?: Partial<ICheckInStatusHistoryEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICheckInStatusHistoryEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.checkInStatusId)) {
				this.checkInStatusId = attributes.checkInStatusId;
			}
			if (isNotNullOrUndefined(attributes.checkInStatus)) {
				if (attributes.checkInStatus === null) {
					this.checkInStatus = attributes.checkInStatus;
				} else if (attributes.checkInStatus instanceof Models.CheckInStatusEntity) {
					this.checkInStatus = attributes.checkInStatus;
					this.checkInStatusId = attributes.checkInStatus.id;
				} else {
					this.checkInStatus = new Models.CheckInStatusEntity(attributes.checkInStatus);
					this.checkInStatusId = this.checkInStatus.id;
				}
			}
			if (isNotNullOrUndefined(attributes.userId)) {
				this.userId = attributes.userId;
			}
			if (isNotNullOrUndefined(attributes.user)) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (isNotNullOrUndefined(attributes.bookingId)) {
				this.bookingId = attributes.bookingId;
			}
			if (isNotNullOrUndefined(attributes.booking)) {
				if (attributes.booking === null) {
					this.booking = attributes.booking;
				} else if (attributes.booking instanceof Models.BookingEntity) {
					this.booking = attributes.booking;
					this.bookingId = attributes.booking.id;
				} else {
					this.booking = new Models.BookingEntity(attributes.booking);
					this.bookingId = this.booking.id;
				}
			}
			if (isNotNullOrUndefined(attributes.stopId)) {
				this.stopId = attributes.stopId;
			}
			if (isNotNullOrUndefined(attributes.stop)) {
				if (attributes.stop === null) {
					this.stop = attributes.stop;
				} else if (attributes.stop instanceof Models.StopEntity) {
					this.stop = attributes.stop;
					this.stopId = attributes.stop.id;
				} else {
					this.stop = new Models.StopEntity(attributes.stop);
					this.stopId = this.stop.id;
				}
			}
		}
	}
}
